import React from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../utils/seo";

const TalentPrivacy = () => {
  useSEO({
    title: "Talent Network Privacy Notice | Onward Land Solutions",
    description: "Privacy notice for the Onward Land Talent Network. Learn what information we collect, how we use it, and how to request updates or deletion.",
    canonicalPath: "/talent-network/privacy",
    noIndex: true
  });

  return (
    <section className="pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8">Onward Land Talent Network Privacy Notice</h1>
        <p className="text-slate-400 text-sm mb-10">Last updated: April 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <div>
            <h2 className="text-xl font-semibold text-white mb-3">What Information We Collect</h2>
            <p className="text-slate-300">
              When you submit a talent profile through the Onward Land Talent Network, we collect the information you provide, including your name, email address, phone number, city, state, primary specialty, current availability, resume file, and any optional details you choose to share such as years of experience, willingness to travel, states you are willing to work in, and additional notes.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-3">Why We Collect It</h2>
            <p className="text-slate-300">
              Resumes and contact information are collected for future project consideration. Onward Land Solutions uses this information to identify qualified professionals when project opportunities arise that match a candidate&apos;s geography, specialty, experience, or availability.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-3">How We Use Your Information</h2>
            <p className="text-slate-300">
              Your information is stored securely and reviewed by authorized Onward personnel when future project staffing needs arise. Onward may contact you about future project opportunities that match your profile. Your resume and contact information are not sold to third parties and are not shared publicly.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-3">No Guarantee of Employment or Assignment</h2>
            <p className="text-slate-300">
              Submitting your information to the Onward Land Talent Network does not guarantee employment, assignment, placement, or project work. The Talent Network is a pool of qualified professionals that Onward may draw from when project needs arise. Submission creates no obligation on either party.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-3">Contact About Future Opportunities</h2>
            <p className="text-slate-300">
              By submitting your talent profile, you authorize Onward to contact you about future project opportunities. You may withdraw this authorization at any time by contacting us.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-3">Data Retention</h2>
            <p className="text-slate-300">
              Onward retains talent profile information for as long as it remains relevant for project staffing purposes. Profiles that are no longer active or responsive may be archived but are not automatically deleted unless requested.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-3">Updates and Deletion Requests</h2>
            <p className="text-slate-300">
              You may request updates to your profile, changes to your availability, or complete deletion of your information at any time by contacting:
            </p>
            <p className="text-slate-300 mt-3">
              <a href="mailto:info@onwardland.com" className="text-blue-400 hover:text-blue-300">info@onwardland.com</a>
            </p>
            <p className="text-slate-300 mt-2">
              Deletion requests will be processed within 30 days.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-white mb-3">Who Has Access</h2>
            <p className="text-slate-300">
              Access to talent profile information is limited to authorized Onward Land Solutions personnel involved in project staffing decisions. Your information is not made available to the general public and is not shared with unrelated third parties.
            </p>
          </div>

          <div className="pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Questions about this privacy notice? Contact <a href="mailto:info@onwardland.com" className="text-blue-400 hover:text-blue-300">info@onwardland.com</a>
            </p>
            <p className="text-slate-500 text-sm mt-4">
              <Link to="/talent-network" className="text-blue-400 hover:text-blue-300">Back to Talent Network</Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TalentPrivacy;
